import { useState } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import Input from "@/v2-ui/input"
import Select from "@/v2-ui/select"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import UsageFilterChips, {
  type UsageFilterChipOption,
} from "@/v2-backoffice-client/usage/UsageFilterChips"
import type { UsageInsight } from "@/v2-backoffice-client/usage/scope/usage.scope.insights"
import {
  PLAY_TONE_LABELS,
  PLAY_TONE_ORDER,
  PLAYBOOK_CHAPTER_LABELS,
  PLAYBOOK_CHAPTER_LEADS,
  PLAYBOOK_PREVIEW_CUSTOMERS,
  PLAYBOOK_SORTS,
  type PlaybookSort,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import {
  groupPortfolioPlays,
  type PortfolioPlay,
} from "@/v2-backoffice-client/portfolio/portfolio.plays.utils"
import {
  chapterPlayGroups,
  countPlayAccounts,
  playbookSortLabel,
  sortPlayGroups,
} from "@/v2-backoffice-client/portfolio/portfolio.playbook.utils"
import PortfolioPlaybookCard from "@/v2-backoffice-client/portfolio/PortfolioPlaybookCard"

export type PortfolioPlaybookChaptersProps = {
  /** Every customer's plays (already globally filtered), in urgency order. */
  plays: PortfolioPlay[]
}

const TONES = Object.keys(PLAY_TONE_ORDER) as UsageInsight["tone"][]

// The account cards under the tiles, with the controls that narrow and
// order them: tone chips, a customer search and a sort. The controls' state
// lives here, so a keystroke re-reads this list and nothing above it.
function PortfolioPlaybookChapters(props: PortfolioPlaybookChaptersProps) {
  const { plays } = props

  const [toneFilter, setToneFilter] = useState<UsageInsight["tone"] | null>(
    null,
  )
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState<PlaybookSort>("urgency")
  const [showAll, setShowAll] = useState(false)

  const searchTerm = search.trim().toLowerCase()
  const scoped = plays.filter(
    (play) =>
      searchTerm === "" || play.customerName.toLowerCase().includes(searchTerm),
  )
  const filtered =
    toneFilter == null
      ? scoped
      : scoped.filter((play) => play.tone === toneFilter)

  const groups = sortPlayGroups(groupPortfolioPlays(filtered), sort)
  // Chapters are cut from the whole list, so a chapter's count is the real
  // one; each chapter then shows its first page until "Show all".
  const chapters = chapterPlayGroups(groups, sort).map((chapter) => ({
    ...chapter,
    shown: showAll
      ? chapter.groups
      : chapter.groups.slice(0, PLAYBOOK_PREVIEW_CUSTOMERS),
  }))
  const hiddenGroupCount = chapters.reduce(
    (sum, chapter) => sum + chapter.groups.length - chapter.shown.length,
    0,
  )

  // Chips count accounts, like the cards they reveal and the tiles above.
  const toneOptions: UsageFilterChipOption<UsageInsight["tone"]>[] = [
    { label: `All (${formatNumber(countPlayAccounts(scoped))})`, value: null },
    ...TONES.map((tone) => ({
      label: `${PLAY_TONE_LABELS[tone]} (${formatNumber(
        countPlayAccounts(scoped.filter((play) => play.tone === tone)),
      )})`,
      value: tone,
    })),
  ]

  return (
    <div className="flex flex-col gap-6">
      <div className="usage-report-section-controls">
        <UsageFilterChips
          options={toneOptions}
          value={toneFilter}
          onChange={(value) => setToneFilter(value)}
        />
        <div className="w-44">
          <Input
            type="search"
            size="xs"
            placeholder="Filter by customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-36 shrink-0">
          <Select
            value={sort}
            onValueChange={(value) => setSort(value as PlaybookSort)}
          >
            <Select.Trigger size="xs">
              <Select.Value placeholder="Sort" />
            </Select.Trigger>
            <Select.Content>
              {PLAYBOOK_SORTS.map((option) => (
                <Select.Item key={option.key} value={option.key}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="usage-report-empty">
          {plays.length === 0
            ? "No plays right now: the usage signals look healthy across the customers in scope."
            : "No account matches the filters."}
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {chapters.map((chapter) => (
            <UsageReportSection
              key={chapter.key ?? "all"}
              level="subsection"
              title={
                chapter.key == null
                  ? "All accounts"
                  : PLAYBOOK_CHAPTER_LABELS[chapter.key]
              }
              lead={
                chapter.key == null
                  ? `Sorted ${playbookSortLabel(sort).toLowerCase()}.`
                  : PLAYBOOK_CHAPTER_LEADS[chapter.key]
              }
              aside={
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatNumber(chapter.groups.length)}{" "}
                  {chapter.groups.length === 1 ? "account" : "accounts"}
                  {chapter.shown.length < chapter.groups.length &&
                    `, ${formatNumber(chapter.shown.length)} shown`}
                </span>
              }
            >
              <div className="usage-report-grid items-start">
                {chapter.shown.map((group) => (
                  <PortfolioPlaybookCard
                    key={group.customerExternalId}
                    group={group}
                  />
                ))}
              </div>
            </UsageReportSection>
          ))}

          {(hiddenGroupCount > 0 || showAll) && (
            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll
                ? "Show fewer"
                : `Show all ${formatNumber(groups.length)} accounts (${formatNumber(hiddenGroupCount)} more)`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default PortfolioPlaybookChapters
